import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthQuery } from "../lib/api/auth";
import { useDonorQuery, useDonorsQuery, type DonorSummary } from "../lib/api/donors";
import { useProgrammesOverviewQuery } from "../lib/api/overview";
import { formatMoney } from "../lib/format/money";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Notice } from "../components/ui/Notice";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageHeader } from "../components/ui/PageHeader";
import { CreateDonorModal } from "../features/donors/CreateDonorModal";
import { DonorDetailModal } from "../features/donors/DonorDetailModal";
import { canManageDonors } from "../features/donors/donor-utils";
import { DonorFundModal } from "../features/donors/DonorFundModal";
import { flattenProgrammeUnits } from "../features/transactions/transaction-utils";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";

const AVATAR_COLORS = ["#9d79b9", "#4c7c9d", "#16877c", "#b38c42"];

function donorInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
}

function thirdMetric(donor: DonorSummary, allScope: boolean): { label: string; value: string } {
  if (allScope) {
    return {
      label: "Outstanding pledge",
      value: formatMoney(donor.outstandingPledge),
    };
  }
  return {
    label: "Approved unpaid",
    value: formatMoney(donor.approvedUnpaid),
  };
}

export function DonorsPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDonorId, setSelectedDonorId] = useState<string | null>(null);
  const [fundModal, setFundModal] = useState<"fund" | "pledge" | null>(null);

  const { period } = useReportingPeriod();
  const donors = useDonorsQuery(Boolean(user));
  const donorDetail = useDonorQuery(selectedDonorId);
  const programmes = useProgrammesOverviewQuery(Boolean(user), period);
  const units = useMemo(
    () => flattenProgrammeUnits(programmes.data?.groups),
    [programmes.data?.groups],
  );

  const canManage = user ? canManageDonors(user.role) : false;
  const allScope = user?.permissions.unitScope === "all";

  return (
    <div>
      <PageHeader
        eyebrow="Donors"
        title="Support you can account for."
        description="Keep commitments, received funds and actual utilization distinct."
        actions={
          canManage ? (
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              Add donor
            </Button>
          ) : undefined
        }
      />

      {!allScope ? (
        <Notice className="mb-6">
          Showing receipts and utilization within your assigned programmes. Full donor commitments
          are available to users with access to the complete fund.
        </Notice>
      ) : null}

      {donors.isLoading ? (
        <LoadingSpinner label="Loading donors…" className="py-16" />
      ) : donors.isError ? (
        <EmptyState
          title="Could not load donors"
          description="Check that the API is running and try again."
        />
      ) : donors.data?.donors.length ? (
        <div className="grid gap-[18px] md:grid-cols-2 xl:grid-cols-3">
          {donors.data.donors.map((donor, index) => {
            const metric = thirdMetric(donor, allScope);
            return (
              <Card
                key={donor.id}
                padding="md"
                className="flex cursor-pointer flex-col gap-[18px] transition-shadow hover:shadow-md"
                onClick={() => {
                  setSelectedDonorId(donor.id);
                }}
              >
                <div className="flex items-center gap-[11px]">
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-sm font-semibold"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${AVATAR_COLORS[index % AVATAR_COLORS.length]} 12%, white)`,
                      color: AVATAR_COLORS[index % AVATAR_COLORS.length],
                    }}
                  >
                    {donorInitials(donor.name)}
                  </span>
                  <div>
                    <h2 className="text-base font-semibold text-edlab-ink">{donor.name}</h2>
                    <p className="text-xs text-edlab-muted">{donor.type}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <small className="block text-xs text-[#86909b]">Received</small>
                    <b className="mt-[5px] block text-[19px] font-semibold tracking-[-0.5px] text-edlab-ink">
                      {formatMoney(donor.received)}
                    </b>
                  </div>
                  <div className="text-right">
                    <small className="block text-xs text-[#86909b]">Unspent funds</small>
                    <b className="mt-[5px] block text-[19px] font-semibold tracking-[-0.5px] text-edlab-green">
                      {formatMoney(donor.unspent)}
                    </b>
                  </div>
                </div>
                <div className="space-y-0 border-t border-edlab-line pt-3">
                  <div className="flex items-center justify-between py-3 text-sm">
                    <span className="text-xs text-edlab-muted">Paid utilization</span>
                    <b className="font-semibold text-edlab-ink">{formatMoney(donor.utilized)}</b>
                  </div>
                  <div className="flex items-center justify-between border-t border-edlab-line py-3 text-sm">
                    <span className="text-xs text-edlab-muted">{metric.label}</span>
                    <b className="font-semibold text-edlab-ink">{metric.value}</b>
                  </div>
                </div>
                <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-edlab-line pt-4">
                  <Button
                    variant="ghost"
                    className="px-0 text-edlab-green hover:bg-transparent"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedDonorId(donor.id);
                    }}
                  >
                    View details →
                  </Button>
                  <Link
                    to={`/reports?type=donor&donor=${donor.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <Button
                      variant="ghost"
                      className="px-0 text-edlab-muted hover:bg-transparent"
                    >
                      Donor report →
                    </Button>
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title="No donors in scope"
          description={
            canManage
              ? "Add a donor, then link a restricted fund to one or more programmes."
              : "Donors appear when they have restricted funds linked to programmes you can access."
          }
          action={
            canManage ? (
              <Button
                onClick={() => {
                  setCreateOpen(true);
                }}
              >
                Add donor
              </Button>
            ) : undefined
          }
        />
      )}

      <CreateDonorModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={(donorId) => {
          setSelectedDonorId(donorId);
        }}
      />

      <DonorDetailModal
        donorId={selectedDonorId}
        onClose={() => {
          setSelectedDonorId(null);
          setFundModal(null);
        }}
        canManage={canManage}
        onAddFund={() => {
          setFundModal("fund");
        }}
        onAddPledge={() => {
          setFundModal("pledge");
        }}
      />

      {selectedDonorId && fundModal ? (
        <DonorFundModal
          open={Boolean(fundModal)}
          onClose={() => {
            setFundModal(null);
          }}
          donorId={selectedDonorId}
          donorName={donorDetail.data?.donor.name ?? "Donor"}
          units={units}
          funds={(donorDetail.data?.donor.funds ?? []).map((fund) => ({
            id: fund.id,
            name: fund.name,
          }))}
          mode={fundModal}
        />
      ) : null}
    </div>
  );
}
